import type { SimTramoPerson, SimTramoPuzzle } from '@cruz/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Portrait } from './Cast.tsx';
import { num } from './format.ts';
import { card, primaryButton, secondaryButton } from './ui.tsx';

/*
 * The board of «Ordena el tramo»: tap the nazarenos in the order they leave the church.
 * The order comes with the puzzle and the server scores the taps again; here the
 * feedback is instant and nothing is kept until the end. The player's daily game and a
 * friend's challenge both play on it.
 */

export const seconds = (ms: number) =>
  (ms / 1000).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "1 fallo", "sin fallos" */
export const mistakes = (n: number) =>
  n === 0 ? 'sin fallos' : n === 1 ? '1 fallo' : `${n} fallos`;

const firstName = (p: SimTramoPerson) => p.name.split(' ')[0]!;

function kindLabel(p: SimTramoPerson): string {
  if (p.me) return 'Con tu cirio';
  if (p.kind === 'insignia') return `Insignia: ${p.note ?? ''}`;
  if (p.kind === 'child') return p.gender === 'f' ? 'Niña' : 'Niño';
  if (p.kind === 'cross') return 'Penitente con cruz';
  return p.gender === 'f' ? 'Nazarena' : 'Nazareno';
}

/** Why that card does not go now: a hint, never the answer. */
function hint(tapped: SimTramoPerson, expected: SimTramoPerson, puzzle: SimTramoPuzzle): string {
  const who = tapped.me ? 'Tú vas' : `${firstName(tapped)} va`;
  if (expected.kind === 'insignia') return 'Primero, la insignia: abre el tramo.';
  if (tapped.kind === 'cross' && expected.kind !== 'cross')
    return 'Las cruces cierran el tramo: van al final.';
  if (tapped.kind === 'child') {
    const parent = puzzle.people.find(
      (p) => p.id === puzzle.order[puzzle.order.indexOf(tapped.id) + 1],
    );
    return `${firstName(tapped)} va justo delante de ${parent ? firstName(parent) : 'su familia'}, no antes.`;
  }
  if (expected.kind === 'child')
    return `Antes, ${firstName(expected)}: va justo delante de su ${expected.note?.includes('madre') ? 'madre' : 'padre'}.`;
  if (tapped.number < expected.number) {
    const old = tapped.me
      ? 'eres más antiguo'
      : `es más ${tapped.gender === 'f' ? 'antigua' : 'antiguo'}`;
    return tapped.me
      ? `Todavía no: ${old} que otros.`
      : `${firstName(tapped)} ${old}: va más atrás.`;
  }
  return `${who} más adelante.`;
}

export function Intro({
  puzzle,
  onStart,
  onSkip,
  kicker = `Minijuego · Nivel ${puzzle.level}`,
  note = `Tienes ${puzzle.seconds} segundos. Una partida al día.`,
}: {
  puzzle: SimTramoPuzzle;
  onStart: () => void;
  onSkip?: () => void;
  kicker?: string;
  note?: string;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => titleRef.current?.focus({ preventScroll: true }), []);
  return (
    <section aria-labelledby="tramo-title" className={`${card} space-y-4 px-5 py-5`}>
      <div>
        <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
          {kicker}
        </p>
        <h1
          id="tramo-title"
          ref={titleRef}
          tabIndex={-1}
          className="mt-1 font-serif text-3xl leading-tight font-semibold outline-none"
        >
          {puzzle.title}
        </h1>
      </div>
      <div className="flex items-start gap-3">
        {puzzle.deputy && (
          <Portrait
            id="tramo_deputy"
            name={puzzle.deputy.name}
            src={puzzle.deputy.portrait}
            size={52}
          />
        )}
        <p className="text-[1.05rem] leading-relaxed">{puzzle.intro}</p>
      </div>
      <div className="rounded-xl bg-cream-200 px-4 py-3 dark:bg-night-700">
        <p className="font-semibold">Toca a cada uno en el orden en que sale:</p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {puzzle.rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>
      <p className="text-ink-soft dark:text-cream/75">{note}</p>
      <button type="button" className={primaryButton} onClick={onStart}>
        Empezar
      </button>
      {onSkip && (
        <button type="button" className={secondaryButton} onClick={onSkip}>
          Ahora no, luego
        </button>
      )}
    </section>
  );
}

export function Board({
  puzzle,
  onFinish,
}: {
  puzzle: SimTramoPuzzle;
  onFinish: (taps: string[], ms: number) => void;
}) {
  const byId = useMemo(() => new Map(puzzle.people.map((p) => [p.id, p])), [puzzle]);
  const [placed, setPlaced] = useState(0);
  const [errors, setErrors] = useState(0);
  const [wrong, setWrong] = useState<{ id: string; at: number } | null>(null);
  const [message, setMessage] = useState('');
  const [left, setLeft] = useState(puzzle.seconds * 1000);
  const taps = useRef<string[]>([]);
  const started = useRef(0);
  const boardRef = useRef<HTMLElement>(null);
  const done = useRef(false);
  useEffect(() => {
    started.current = performance.now();
    // The board fills the screen: the status bar stays above, out of the way
    boardRef.current?.scrollIntoView({ block: 'start' });
  }, []);

  const end = useCallback(() => {
    if (done.current) return;
    done.current = true;
    onFinish(taps.current, Math.round(performance.now() - started.current));
  }, [onFinish]);

  useEffect(() => {
    const t = setInterval(() => {
      const rest = puzzle.seconds * 1000 - (performance.now() - started.current);
      setLeft(Math.max(0, rest));
      if (rest <= 0) end();
    }, 100);
    return () => clearInterval(t);
  }, [puzzle.seconds, end]);

  const late = puzzle.latecomer;
  const lateIn = !late || placed >= late.after;
  const placedIds = new Set(puzzle.order.slice(0, placed));
  const waiting = puzzle.people.filter(
    (p) => !placedIds.has(p.id) && (lateIn || p.id !== late?.id),
  );

  const tap = (p: SimTramoPerson) => {
    if (done.current) return;
    taps.current.push(p.id);
    const expected = byId.get(puzzle.order[placed]!)!;
    if (p.id === expected.id) {
      const next = placed + 1;
      setPlaced(next);
      setWrong(null);
      setMessage(
        next === puzzle.order.length
          ? '¡Tramo formado!'
          : `${p.me ? 'Tú, a tu sitio' : `${firstName(p)}, a su sitio`}.`,
      );
      if (next === puzzle.order.length) end();
    } else {
      setErrors((e) => e + 1);
      setWrong((w) => ({ id: p.id, at: (w?.at ?? 0) + 1 }));
      setMessage(hint(p, expected, puzzle));
      navigator.vibrate?.(30);
    }
  };

  const share = left / (puzzle.seconds * 1000);
  const lateArrived = late && lateIn && !placedIds.has(late.id) ? byId.get(late.id) : null;
  return (
    <section ref={boardRef} aria-labelledby="board-title" className="scroll-mt-0 space-y-2">
      <h1 id="board-title" className="sr-only">
        {puzzle.title}: toca a cada uno en el orden en que sale
      </h1>
      <div className="sticky top-0 z-10 -mx-4 bg-cream/95 px-4 pt-2 pb-3 backdrop-blur dark:bg-night/95">
        <div className="flex items-baseline justify-between">
          <p className="font-serif text-3xl font-semibold tabular" aria-hidden>
            {Math.ceil(left / 1000)} s
          </p>
          <p className="text-ink-soft dark:text-cream/75">
            <span className="tabular">{placed}</span> de {puzzle.order.length} ·{' '}
            {errors === 1 ? '1 fallo' : `${errors} fallos`}
          </p>
        </div>
        <div
          className="mt-1.5 h-2 overflow-hidden rounded-full bg-cream-300 dark:bg-night-700"
          role="progressbar"
          aria-label="Tiempo"
          aria-valuemin={0}
          aria-valuemax={puzzle.seconds}
          aria-valuenow={Math.ceil(left / 1000)}
        >
          <div
            className={`h-full rounded-full transition-[width] duration-100 ${share < 0.25 ? 'bg-burgundy dark:bg-burgundy-300' : 'bg-gold'}`}
            style={{ width: `${share * 100}%` }}
          />
        </div>
        <ol
          aria-label="Tu tramo, de delante a atrás"
          className="mt-2 flex min-h-8 flex-wrap gap-1.5"
        >
          {puzzle.order.slice(0, placed).map((id, i) => {
            const p = byId.get(id)!;
            return (
              <li
                key={id}
                className="animate-rise rounded-full bg-purple px-2.5 py-1 text-xs font-semibold text-cream dark:bg-gold-300 dark:text-night"
              >
                <span className="sr-only">{i + 1}. </span>
                {p.me ? 'Tú' : firstName(p)}
              </li>
            );
          })}
          {placed === 0 && (
            <li className="py-1 text-sm text-ink-soft dark:text-cream/70">
              Aquí se va formando tu tramo.
            </li>
          )}
        </ol>
      </div>

      <p aria-live="polite" className="min-h-6 text-center font-medium">
        {message}
      </p>
      {lateArrived && (
        <p
          role="status"
          className="animate-rise rounded-xl border-l-4 border-burgundy bg-paper px-4 py-2 font-medium dark:border-burgundy-300 dark:bg-night-800"
        >
          ¡Llega tarde {firstName(lateArrived)}, sin aliento! Ponlo en su sitio.
        </p>
      )}
      <ul className="grid grid-cols-2 gap-2">
        {waiting.map((p) => {
          const shaking = wrong?.id === p.id;
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => tap(p)}
                aria-label={`${p.me ? 'Tú' : p.name}, ${kindLabel(p)}, número ${p.number}${p.kind === 'child' && p.note ? `. ${p.note}` : ''}`}
                className={`${shaking ? 'animate-shake border-burgundy dark:border-burgundy-300' : p.id === late?.id ? 'animate-rise border-gold' : 'border-purple/15 dark:border-cream/15'} flex min-h-[4.75rem] w-full flex-col items-start rounded-xl border-2 bg-paper px-3 py-2 text-left transition-colors active:bg-cream-200 dark:bg-night-800 dark:active:bg-night-700 ${p.me ? 'ring-2 ring-gold' : ''}`}
                key={shaking ? wrong?.at : undefined}
              >
                <span className="text-xs font-semibold tracking-wide text-gold-700 uppercase dark:text-gold-300">
                  {kindLabel(p)}
                </span>
                <span className="mt-0.5 leading-tight font-medium">{p.me ? 'Tú' : p.name}</span>
                {p.kind === 'child' && p.note ? (
                  <span className="mt-0.5 text-sm text-ink-soft dark:text-cream/75">{p.note}</span>
                ) : (
                  <span className="font-serif text-xl leading-tight font-semibold tabular">
                    nº {num(p.number)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
